import React, { useState, useEffect } from 'react';
import { View, Animated, Dimensions, StyleSheet, ScrollView } from 'react-native';
import BottomSheet from '../../../../components/Interface/LegacyBottomSheet';
import { RFValue } from 'react-native-responsive-fontsize';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderBackButton,
  BackIcon,
  FilterContainer,
  FilterCard,
  Filter,
  FilterText,
  Icon,
  FilterRow,
  FilterBadge,
  FilterBadgeText,
  ButtonsContainer,
} from './styles';
import { Button } from '../../../../components/Interface/Forms/Buttons/Button';
import { LinkButton } from '../../../../components/Interface/Forms/Buttons/LinkButton';
import { SafeAreaView } from 'react-native-safe-area-context';
import FilterEstadoContent from './FilterEstadoContent';
import FilterCidadeContent from './FilterCidadeContent';
import FilterBairroContent from './FilterBairroContent';
import FilterCategoryContent from './FilterCategoryContent';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: {
    estado?: number;
    cidade?: number;
    bairro?: number;
    categoria?: string;
  }) => void;
  initialFilters?: {
    estado?: number;
    cidade?: number;
    bairro?: number;
    categoria?: string;
  };
}

type FilterStep = 'main' | 'estado' | 'cidade' | 'bairro' | 'categoria';

export default function FiltersBottomSheet({
  isOpen,
  onClose,
  onApply,
  initialFilters = {},
}: Props) {
  const [currentStep, setCurrentStep] = useState<FilterStep>('main');
  const [filters, setFilters] = useState(initialFilters);

  const slideAnim = React.useRef(new Animated.Value(0)).current;
  const screenWidth = Dimensions.get('window').width;

  const animateSlide = (direction: number, callback?: () => void) => {
    Animated.timing(slideAnim, {
      toValue: direction * screenWidth,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      callback?.();
      slideAnim.setValue(-direction * screenWidth);
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    });
  };

  const handleStepChange = (newStep: FilterStep, direction: number = -1) => {
    animateSlide(direction, () => setCurrentStep(newStep));
  };

  const handleFilterChange = (key: string, value: any) => {
    if (key === 'estado' && value !== filters.estado) {
      setFilters({ ...filters, [key]: value, cidade: undefined, bairro: undefined });
    } else if (key === 'cidade' && value !== filters.cidade) {
      setFilters({ ...filters, [key]: value, bairro: undefined });
    } else {
      setFilters({ ...filters, [key]: value });
    }
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const getHeaderTitle = () => {
    switch (currentStep) {
      case 'estado':
        return 'Estado';
      case 'cidade':
        return 'Cidade';
      case 'bairro':
        return 'Bairro';
      case 'categoria':
        return 'Categoria';
      default:
        return 'Filtrar parceiros';
    }
  };

  const showHeader = currentStep !== 'main';

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('main');
      slideAnim.setValue(screenWidth);
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isOpen]);



  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <FilterContainer>
        {showHeader && (
          <HeaderContainer>
            <HeaderBackButton onPress={() => handleStepChange('main', 1)}>
              <BackIcon name="chevron-left" />
            </HeaderBackButton>
            <HeaderTitle>{getHeaderTitle()}</HeaderTitle>
            <View style={{ width: RFValue(24) }} />
          </HeaderContainer>
        )}

        <View style={styles.animatedContainer}>
          <Animated.View
            style={[
              styles.animatedView,
              { transform: [{ translateX: slideAnim }] },
            ]}>
            {currentStep === 'main' && (
              <ScrollView
                scrollEnabled
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.mainContent}>
                <FilterCard>
                  <Filter activeOpacity={1} onPress={() => handleStepChange('estado')}>
                    <FilterRow>
                      <FilterText>Estado</FilterText>
                      {filters.estado && (
                        <FilterBadge>
                          <FilterBadgeText>1 selecionado</FilterBadgeText>
                        </FilterBadge>
                      )}
                    </FilterRow>
                    <Icon name="chevron-right" />
                  </Filter>

                  {filters.estado && (
                    <Filter activeOpacity={1} onPress={() => handleStepChange('cidade')}>
                      <FilterRow>
                        <FilterText>Cidade</FilterText>
                        {filters.cidade && (
                          <FilterBadge>
                            <FilterBadgeText>1 selecionado</FilterBadgeText>
                          </FilterBadge>
                        )}
                      </FilterRow>
                      <Icon name="chevron-right" />
                    </Filter>
                  )}

                  {filters.estado && filters.cidade && (
                    <Filter activeOpacity={1} onPress={() => handleStepChange('bairro')}>
                      <FilterRow>
                        <FilterText>Bairro</FilterText>
                        {filters.bairro && (
                          <FilterBadge>
                            <FilterBadgeText>1 selecionado</FilterBadgeText>
                          </FilterBadge>
                        )}
                      </FilterRow>
                      <Icon name="chevron-right" />
                    </Filter>
                  )}

                  <Filter
                    activeOpacity={1}
                    onPress={() => handleStepChange('categoria')}
                    style={{ borderBottomWidth: 0 }}>
                    <FilterRow>
                      <FilterText>Categoria</FilterText>
                      {filters.categoria && (
                        <FilterBadge>
                          <FilterBadgeText>1 selecionada</FilterBadgeText>
                        </FilterBadge>
                      )}
                    </FilterRow>
                    <Icon name="chevron-right" />
                  </Filter>
                </FilterCard>

                <View style={{flexDirection: 'row' }}>
                  <LinkButton
                    text="Limpar filtro"
                    enabled
                    action={handleClearFilters}
                  />
                </View>

              </ScrollView>
            )}

            {currentStep === 'estado' && (
              <FilterEstadoContent
                estado={filters.estado}
                onSelectEstado={(value: any) => {
                  handleFilterChange('estado', value);
                  handleStepChange('main', 1);
                }}
              />
            )}

            {currentStep === 'cidade' && filters.estado && (
              <FilterCidadeContent
                estado={filters.estado}
                cidade={filters.cidade}
                onSelectCidade={(value: any) => {
                  handleFilterChange('cidade', value);
                  handleStepChange('main', 1);
                }}
              />
            )}

            {currentStep === 'bairro' && filters.estado && filters.cidade && (
              <FilterBairroContent
                cidade={filters.cidade}
                bairro={filters.bairro}
                onSelectBairro={(value: any) => {
                  handleFilterChange('bairro', value);
                  handleStepChange('main', 1);
                }}
              />
            )}

            {currentStep === 'categoria' && (
              <FilterCategoryContent
                categoria={filters.categoria}
                onSelectCategoria={(value: any) => {
                  handleFilterChange('categoria', value);
                  handleStepChange('main', 1);
                }}
              />
            )}
          </Animated.View>
        </View>

        {currentStep === 'main' && (
          <ButtonsContainer>
            <SafeAreaView>
              <Button
                title="Aplicar filtros"
                enabled={filters.categoria !== undefined || filters.estado !== undefined}
                onPress={handleApply}
              />
            </SafeAreaView>
          </ButtonsContainer>
        )}
      </FilterContainer>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  animatedContainer: {
    overflow: 'hidden',
  },
  animatedView: {
    width: '100%',
  },
  mainContent: {
    paddingHorizontal: RFValue(24),
    paddingVertical: RFValue(16),
    paddingBottom: RFValue(20),
  },
});
